<template>
  <UserCommonLayout :is-open-filter="isOpenFilterFlag" :having-filter="true">
    <template #sidebar>
      <div v-if="isLoading" class="sideWrap">
        <LazySkillAdvanceCommonFilter
          @filter-toggle-on-class="filterToggleOnClass"/>
        <div class="rightContWrap">
          <main id="dashboardContent">
            <LazySkillAdvanceList
              :is-open-filter="isOpenFilterFlag"
              :advance-page-data="advances"
              :advance-type="SkillAdvanceType.PRACTICE"
              @emit-track-list="getAdvanceList"
              @filter-toggle-on-class="filterToggleOnClass"
            />
          </main>
          <LazyUserCommonFooter />
        </div>
      </div>
    </template>
  </UserCommonLayout>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, useRoute, watch} from "#imports";
import {LazySkillAdvanceCommonFilter, LazySkillAdvanceList, UserCommonLayout} from "#components";
import {useTrackService} from "~/composables/service/useTrackService";
import {useLoadingStore} from "~/store/loading/loadingStore";
import {PageModel} from "~~/package/mapper/common/model/PageModel";
import type TrackResponse from "~~/package/apis/track/response/TrackResponse";
import {useI18n} from "vue-i18n";
import {SkillAdvanceType} from "~~/package/mapper/track/model/SkillAdvanceType";

const { t } = useI18n();
const route = useRoute();
const loadingStore = useLoadingStore();

const isLoading = ref(true);
const advances = ref(new PageModel<TrackResponse>());
const page = ref(1);

const isOpenFilterFlag = ref(false);

onMounted(() => {
  isOpenFilterFlag.value = window.innerWidth > 1080;
});
const field = ref<string | undefined>(undefined);

const filters = computed(() => {
  if (!route.query.filter) return [];

  return Array.isArray(route.query.filter)
    ? route.query.filter
    : [route.query.filter];
});

const transFilterString = computed(() => {
  const filterMap = new Map<string, string>();
  let hasFieldKey = false;

  filters.value.forEach((filter) => {
    const [key, value] = (filter as string).split(":");
    if (key === "field") {
      field.value = value;
      hasFieldKey = true;
    } else {
      const existingValue = filterMap.get(key);
      filterMap.set(key, existingValue ? `${existingValue}|${value}` : value);
    }
  });

  if (!hasFieldKey) {
    field.value = undefined;
  }

  return Array.from(filterMap, ([key, value]) => `${key}:${value}`);
});

const fetchAdvanceList = (pageNum: number, filter: string[]) => {
  return useTrackService().getAdvanceList(
    filter,
    pageNum,
    9,
    SkillAdvanceType.PRACTICE,
    field.value
  );
};

const initData = (filter: string[]) => {
  fetchAdvanceList(1, filter).then((resolve) => {
    advances.value = resolve;
    page.value = 2;
    loadingStore.closeLoading();
    isLoading.value = !loadingStore.show;
  })
};

const getAdvanceList = () => {
  fetchAdvanceList(page.value, transFilterString.value).then((resolve) => {
    if (page.value !== 1) {
      resolve.data = [...advances.value.data, ...resolve.data];
    }
    advances.value = resolve;
    page.value += 1;
  });
};

const filterToggleOnClass = (flag: boolean) => {
  isOpenFilterFlag.value = flag;
}

watch(() => route.query, () => {
  page.value = 1;
  initData(transFilterString.value);
}, { immediate: true });
</script>
